import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["all.color", "color"],
  _excluded2 = ["color"];
import { BORDER, COLOR, GROUP } from 'ContentUtils/constants/CustomWidgetFieldTypes';
import { modifyInheritedValue } from 'ContentComponents/components/cmv2/utils/inheritance';
import CustomWidgetModifierOperators from 'ContentUtils/constants/CustomWidgetModifierOperators';
import { SHADE_POSITION_NAMES } from 'ContentUtils/constants/ColorShadePositions';
export const scanFieldTree = fields => {
  const fieldsToVisit = [...fields];
  let hasCollection = false;
  let hasGlobalStylesBindings = false;
  while (fieldsToVisit.length > 0 && !(hasCollection && hasGlobalStylesBindings)) {
    var _field$binding;
    const field = fieldsToVisit.pop();
    if (field.collection != null) hasCollection = true;
    if (((_field$binding = field.binding) === null || _field$binding === void 0 ? void 0 : _field$binding.context) === 'global_styles') hasGlobalStylesBindings = true;
    if (field.type === GROUP && field.children) {
      fieldsToVisit.push(...field.children);
    }
  }
  return {
    hasCollection,
    hasGlobalStylesBindings
  };
};

/**
 * Workaround: the BE preview-CSS resolver only honors `all.color` for
 * border-field bindings, but the FE (BoundColorPickerPopover + GSS variant
 * defaults) produces `color`-keyed paths to match ColorField's shape.
 * Translate at this boundary so persisted state stays in the BE-aligned
 * `all.color` shape, while the inner ColorPicker keeps reading `color`.
 *
 * Tracking: https://github.com/HubSpotEngineering/ReusableAssetsTeam/issues/1238
 */
const borderBindingForDisplay = binding => {
  const paths = binding === null || binding === void 0 ? void 0 : binding.property_value_paths;
  if (!paths || !paths['all.color']) return binding;
  // If both `all.color` and `color` are present, prefer `all.color`
  const {
      'all.color': allColor
    } = paths,
    rest = _objectWithoutPropertiesLoose(paths, _excluded);
  return Object.assign({}, binding, {
    property_value_paths: Object.assign({}, rest, {
      color: allColor
    })
  });
};
const borderBindingForWire = ref => {
  var _rest$allColor;
  const paths = ref === null || ref === void 0 ? void 0 : ref.property_value_paths;
  if (!paths || !paths.color) return ref;
  const {
      color
    } = paths,
    rest = _objectWithoutPropertiesLoose(paths, _excluded2);
  return Object.assign({}, ref, {
    property_value_paths: Object.assign({}, rest, {
      'all.color': (_rest$allColor = rest['all.color']) !== null && _rest$allColor !== void 0 ? _rest$allColor : color
    })
  });
};
export const resolveFieldBindingProps = (field, fieldKey, context) => {
  const {
    binding
  } = field;
  if (!binding || !context) return {};
  const {
    bindings,
    collectionMap,
    isGlobalStylesLoading,
    onBindingChange
  } = context;
  if (binding.context === 'global_styles' && isGlobalStylesLoading) {
    return {
      isBindingLoading: true
    };
  }
  const fieldPath = fieldKey.join('.');
  const collectionData = collectionMap[binding.collection];
  const isBorderField = field.type === BORDER;
  const rawBinding = bindings[fieldPath];
  return {
    collectionData,
    currentBinding: isBorderField ? borderBindingForDisplay(rawBinding) : rawBinding,
    onBindingChange: ref => onBindingChange(fieldPath, isBorderField ? borderBindingForWire(ref) : ref)
  };
};

/**
 * Maps each bindable field type to the properties on its value object that hold collectible values.
 * Each entry is a string array naming the sub-properties to extract (e.g. ['color'] → fieldValue.color).
 *
 * An entry is required for any field type that should participate in collections.
 * Field types without an entry are skipped entirely during collection map building.
 */
const COLLECTION_VALUE_PROP_BY_FIELD_TYPE = {
  [COLOR]: ['color']
};

/**
 * Traverses a field tree and builds a map of collection IDs to property path → resolved value.
 * Used to generate the runtime lookup table for binding resolution.
 *
 * @param fields - The field spec to traverse
 * @param overrides - Current user-set values (mirrors module_body shape)
 * @param pathPrefix - Dot-path segments prepended to every entry path. Recursion uses this
 *   to accumulate parent group names; GS callers also pass `['theme']` at the top level
 *   to match the BE's `theme.`-prefixed binding-path wire format (TEMP, RAT #1238).
 * @returns A CollectionMap keyed by collection ID, then by dotted property path to resolved value
 *
 * @example
 * const map = buildCollectionMap(fields, moduleOverrides);
 * // { theme_colors: { "palette.primary_accent.color": "#FF5733" } }
 */
export const buildCollectionMap = (fields, overrides = {}, pathPrefix = []) => {
  const collectionMap = fields.reduce((acc, field) => {
    const propertyKey = COLLECTION_VALUE_PROP_BY_FIELD_TYPE[field.type];
    // Skip non-group fields with no collection or an unsupported field type
    if (field.type !== GROUP && (!field.collection || propertyKey === undefined)) {
      return acc;
    }

    // Resolve the effective value
    let value = overrides[field.name];
    if (value === undefined) {
      value = field.default;
    } else if (field.default && !Array.isArray(field.default) &&
    // fields with array defaults (e.g. occurrence groups, multi-choice) — not supported
    typeof field.default === 'object' && typeof value === 'object') {
      // Merge partial overrides
      value = Object.assign({}, field.default, value);
    }
    if (field.type === GROUP) {
      if (field.children) {
        const childCollectionMap = buildCollectionMap(field.children, value || {}, [...pathPrefix, field.name]);
        // Merge child collection entries into the parent map
        for (const collectionId of Object.keys(childCollectionMap)) {
          var _existing$resolvedVal, _existing$cssVariable, _existing$renderShade;
          const existing = acc[collectionId];
          const child = childCollectionMap[collectionId];
          acc[collectionId] = {
            resolvedValues: Object.assign({}, (_existing$resolvedVal = existing === null || existing === void 0 ? void 0 : existing.resolvedValues) !== null && _existing$resolvedVal !== void 0 ? _existing$resolvedVal : {}, child.resolvedValues),
            cssVariables: Object.assign({}, (_existing$cssVariable = existing === null || existing === void 0 ? void 0 : existing.cssVariables) !== null && _existing$cssVariable !== void 0 ? _existing$cssVariable : {}, child.cssVariables),
            renderShades: ((_existing$renderShade = existing === null || existing === void 0 ? void 0 : existing.renderShades) !== null && _existing$renderShade !== void 0 ? _existing$renderShade : false) || child.renderShades
          };
        }
      }
      return acc;
    }
    // Add entry to collection map
    const collectionId = field.collection;
    for (const propKey of propertyKey) {
      var _value;
      const propValue = (_value = value) === null || _value === void 0 ? void 0 : _value[propKey];
      if (propValue != null && field.type === COLOR) {
        if (!acc[collectionId]) {
          acc[collectionId] = {
            resolvedValues: {},
            cssVariables: {},
            renderShades: false
          };
        }
        if (field.render_shades) {
          acc[collectionId].renderShades = true;
        }
        const entryPath = [...pathPrefix, field.name, propKey].join('.');
        acc[collectionId].resolvedValues[entryPath] = propValue;
        if (field.css_variable) {
          acc[collectionId].cssVariables[entryPath] = field.css_variable;
        }
      }
    }
    return acc;
  }, {});
  return collectionMap;
};
const normalizeCssVarName = name => name.startsWith('--') ? name : `--${name}`;
export function inheritedValueToCssVar(binding, collectionData) {
  var _binding$property_val;
  const path = (_binding$property_val = binding.property_value_paths) === null || _binding$property_val === void 0 ? void 0 : _binding$property_val.color;
  if (!path) return undefined;
  const baseCssVar = collectionData.cssVariables[path];
  if (!baseCssVar) return undefined;
  const normalizedVar = normalizeCssVarName(baseCssVar);
  const {
    modifier
  } = binding;
  if (!modifier || modifier.modifier_type !== CustomWidgetModifierOperators.SHADE) {
    return `var(${normalizedVar})`;
  }
  return `var(${normalizedVar}-${modifier.modifier_value.toLowerCase()})`;
}
export function cssVarToInheritedValue(cssVar, collectionData) {
  var _entries$find;
  // Matches CSS var() syntax — e.g. `var(--hs-primary-color)` — and captures the name inside the parens
  const match = cssVar.match(/^var\(([^)]+)\)$/);
  if (!match) return undefined;
  const varName = match[1].trim();
  const entries = Object.entries(collectionData.cssVariables);
  const cssVarEntry = (_entries$find = entries.find(([, rawBase]) => varName === normalizeCssVarName(rawBase))) !== null && _entries$find !== void 0 ? _entries$find : entries.find(([, rawBase]) => varName.startsWith(`${normalizeCssVarName(rawBase)}-`));
  if (!cssVarEntry) return undefined;
  const [path, rawBase] = cssVarEntry;
  const normalizedBase = normalizeCssVarName(rawBase);
  if (varName === normalizedBase) {
    // `color` is hardcoded here as the property key because color is currently
    // the only bindable sub-property. Tracking: https://github.com/HubSpotEngineering/ReusableAssetsTeam/issues/1257
    return {
      property_value_paths: {
        color: path
      }
    };
  }
  const suffix = varName.slice(normalizedBase.length + 1);
  const shadePosition = SHADE_POSITION_NAMES.find(pos => pos.toLowerCase() === suffix);
  if (!shadePosition) return undefined;
  return {
    property_value_paths: {
      color: path
    },
    modifier: {
      modifier_type: CustomWidgetModifierOperators.SHADE,
      modifier_value: shadePosition
    }
  };
}

// TODO: The existing inherited_value resolver in inheritance.ts resolves property_value_paths by
// walking module data via getDeepValue. This resolver uses the CollectionMap instead (O(1) lookup).
// Consider whether the two can share logic once the binding feature matures.

/**
 * Resolves all binding paths in a binding reference against a specific collection's entries.
 *
 * The caller is responsible for selecting the correct collection from the CollectionMap
 * using the collection ID from field.binding.collection before calling this function.
 *
 * @param bindingRef - An InheritedValue whose property_value_paths point into the collection
 * @param collectionEntries - The entries for the specific collection: collectionMap[field.binding.collection].entries
 * @returns A map of prop key → resolved value for each path that resolves; empty if nothing resolves
 *
 * @example
 * const resolved = resolveBindingReference(
 *   { property_value_paths: { color: "palette.accent.color" } },
 *   collectionMap[field.binding.collection].entries
 * );
 * // { color: "#FF5733" }
 */
export const resolveBindingReference = (bindingRef, collectionEntries) => {
  const {
    property_value_paths: propertyValuePaths,
    modifier
  } = bindingRef;
  const {
    modifier_type,
    modifier_value
  } = modifier !== null && modifier !== void 0 ? modifier : {};
  // TODO: also support default_value_path as a single direct path into the collection entries,
  // similar to how inheritance.ts uses it for full-value references (useful for scalar collection fields)

  if (!propertyValuePaths) return {};
  const result = {};
  for (const [propKey, sourcePath] of Object.entries(propertyValuePaths)) {
    const resolved = collectionEntries[sourcePath];
    if (resolved == null) continue;
    result[propKey] = modifier_type && modifier_value != null ? modifyInheritedValue(resolved, modifier_type, modifier_value) : resolved;
  }
  return result;
};